import React, {useCallback, useState, useEffect, useContext} from "react";
import moment from "moment";
import "../../company/CompanyAccountProfile/CompanyBriefs/columns.jsx";
import {useRequest} from "../../utils/hooks.jsx";
import {
    BidStatusToColorAdmin,
    briefPageSize,
    BriefsShortcuts,
    BriefStatus,
    BriefStatusToColorAdmin,
    companyBriefProjection,
    companySelectedActions
} from "../../company/CompanyAccountProfile/CompanyBriefs/consts.jsx";
import {columns} from "../../company/CompanyAccountProfile/CompanyBriefs/columns.jsx";
import {BriefBidsReceivedModal} from "../../brief/BidsReceivedModal/BriefBidsReceivedModal.jsx";
import {BriefFormModal} from "../../brief/BriefFormModal/BriefFormModal.jsx";
import {TableDateRangeTable} from "../../components/OrdersDateRangeTable/TableDateRangeTable.jsx";
import {TableDateSearchContext} from "../Context/TableDateSearchContext.jsx";
import {AppContext} from "../../AppContext.jsx";
import {
    updateAdminCommentByAdmin,
    updateAssignedCSMsByAdmin,
    updateBriefStatusByAdmin,
    updateBriefByAdmin,
    cancelBid
} from "../../api/briefs/briefs.js";
import {filterBriefsBySearchInput} from "./utils.jsx";
import {ADMIN_PATH_NAME} from "../consts.jsx";
import {useHistory} from "react-router";
import {NotifyAboutAdminOrderUpdateModal} from "../../orders/orderModals/NotifyAboutAdminOrderUpdateModal.jsx";
import {editOrderNotificationsReceivers} from "../../orders/orderModals/consts.jsx";
import {DeclineOrderModal} from "../../event/DeclineOrderModal/DeclineOrderModal.jsx";
import {
    briefDeclineOptionsToText,
    BRIEF_DECLINE_MODAL_HEADER,
    BRIEF_DECLINE_MODAL_SUBTITLE
} from "../../event/DeclineOrderModal/consts.js";
import {BidStatus} from "../../provider/ProviderBriefs/consts.jsx";
import {BriefChangesHistoryModal} from "./BriefChangesHistoryModal.jsx";

export const AdminBriefs = () => {
    const history = useHistory();
    const {me} = useContext(AppContext);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedBriefAction, setSelectedBriefAction] = useState(null);
    const [selectedActions, setSelectedAction] = useState(null);
    const [innerBriefs, setInnerBriefs] = useState([]);
    const [loadingUpdateBrief, setLoadingUpdateBrief] = useState(null);
    const [searchInput, setSearchInput] = useState(null);
    const {searchDate, searchByCreated, searchStatuses} = useContext(TableDateSearchContext);
    const [briefsDtstartOrder, setBriefsDtstartOrder] = useState(null);
    const [briefsCreatedAtOrder, setBriefsCreatedAtOrder] = useState(null);
    const [briefsSystemIdOrder, setBriefsSystemIdOrder] = useState([]);
    const [isWaitingForAdminApproval, setIsWaitingForAdminApproval] = useState(false);
    const [historyBrief, setHistoryBrief] = useState(null);

    const [briefs, isLoadingBriefs, error, fetchBriefs] = useRequest(
        "/admin/api/briefs",
        "POST",
        {
            page: currentPage,
            pageSize: briefPageSize,
            projection: companyBriefProjection,
            includeBids: true,
            startTime: searchDate[0].valueOf(),
            endTime: searchDate[1].valueOf(),
            byCreatedAt: searchByCreated,
            ...(!!searchInput && {searchInput: searchInput}),
            ...(searchStatuses?.length ? {statuses: searchStatuses} : {}),
            ...(briefsDtstartOrder ? {orderBy: briefsDtstartOrder} : {}),
            ...(briefsCreatedAtOrder ? {orderBy: briefsCreatedAtOrder} : {}),
            ...(briefsSystemIdOrder?.length ? {systemIds: briefsSystemIdOrder} : {}),
            ...(!!isWaitingForAdminApproval ? {awaitingAdminApproval: true} : {})
        },
        [
            currentPage,
            searchDate,
            searchByCreated,
            searchStatuses,
            briefsDtstartOrder,
            briefsCreatedAtOrder,
            briefsSystemIdOrder,
            isWaitingForAdminApproval,
            searchInput
        ],
        searchDate?.length,
        null
    );

    const [systemUsers] = useRequest("/admin/api/users/systemStaffUsers");

    const innerUpdateBrief = useCallback(newBriefInfo => {
        setInnerBriefs(prev => {
            if (!prev?.briefs) return prev;

            const tempPrevBriefs = [...prev.briefs];
            const replaceItemIndex = tempPrevBriefs.findIndex(({briefId}) => newBriefInfo.briefId === briefId);

            if (replaceItemIndex === -1) return prev;

            tempPrevBriefs[replaceItemIndex] = {
                ...tempPrevBriefs[replaceItemIndex],
                ...newBriefInfo
            };

            return {
                ...prev,
                briefs: tempPrevBriefs
            };
        });
    }, []);

    const onUpdateAdminComment = useCallback(
        async (adminComment, briefId, updatedAt) => {
            setLoadingUpdateBrief(briefId);
            setSelectedAction(companySelectedActions.editBrief);
            const selected = innerBriefs?.briefs?.find(b => b.briefId === briefId) ?? null;
            setSelectedBriefAction({
                ...selected,
                date: moment(selected.dtstart),
                time: moment(selected.dtstart)
            });
            const adminCommentToUpdate = adminComment
                ? {
                      content: adminComment,
                      author: me.firstName,
                      lastUpdate: Date.now()
                  }
                : null;

            const res = await updateAdminCommentByAdmin(briefId, adminCommentToUpdate, updatedAt);

            if (res?.error) {
                setLoadingUpdateBrief(null);
                return;
            }

            fetchBriefs();
            setLoadingUpdateBrief(null);
        },
        [fetchBriefs, innerBriefs, me.firstName]
    );

    const onChangeCSM = useCallback(
        async (selectedCsm, currentCSMs = [], briefId, updatedAt) => {
            setLoadingUpdateBrief(briefId);
            setSelectedAction(companySelectedActions.editBrief);
            const selected = innerBriefs?.briefs?.find(b => b.briefId === briefId) ?? null;
            setSelectedBriefAction({
                ...selected,
                date: moment(selected.dtstart),
                time: moment(selected.dtstart)
            });
            const csmExists = currentCSMs.some(({userId}) => userId === selectedCsm.userId);
            const assignedCSMs = csmExists
                ? currentCSMs.filter(({userId}) => userId !== selectedCsm.userId)
                : [...currentCSMs, selectedCsm];
            const toUpdate = {
                briefId,
                assignedCSMs
            };

            const res = await updateAssignedCSMsByAdmin(briefId, assignedCSMs, updatedAt);

            if (res?.error) {
                setLoadingUpdateBrief(null);
                return;
            }

            fetchBriefs();
            innerUpdateBrief(toUpdate);
            setLoadingUpdateBrief(null);
        },
        [fetchBriefs, innerBriefs, innerUpdateBrief]
    );

    const closeActionModal = useCallback(() => {
        setSelectedAction(null);
        setSelectedBriefAction(null);
    }, []);

    const onCancelBrief = useCallback(brief => {
        setSelectedBriefAction(brief);
        setSelectedAction(companySelectedActions.canceledBrief);
    }, []);

    const onDeclineBrief = useCallback(
        async declineDetails => {
            setLoadingUpdateBrief(selectedBriefAction?.briefId);

            const res = await updateBriefStatusByAdmin(
                selectedBriefAction?.briefId,
                BriefStatus.cancelled,
                new Date().getTime(),
                declineDetails.whoToSendUpdateEmail?.length ? declineDetails.whoToSendUpdateEmail : ["none"],
                declineDetails.declineReason,
                declineDetails.declineComment
            );

            if (res?.error) {
                setLoadingUpdateBrief(null);
                return;
            }

            fetchBriefs();
            closeActionModal();
            setLoadingUpdateBrief(null);
        },
        [fetchBriefs, selectedBriefAction?.briefId, closeActionModal]
    );

    const onConfirmBrif = useCallback(
        async brief => {
            setLoadingUpdateBrief(brief.briefId);
            setSelectedAction(companySelectedActions.editBrief);
            setSelectedBriefAction({
                ...brief,
                date: moment(brief.dtstart),
                time: moment(brief.dtstart)
            });
            const briefToUpdate = {
                ...brief,
                watchedByAdmin: true
            };

            const res = await updateBriefByAdmin(brief.briefId, briefToUpdate, brief.updatedAt);

            if (res?.error) {
                setLoadingUpdateBrief(null);
                return;
            }

            fetchBriefs();
            setLoadingUpdateBrief(null);
        },
        [fetchBriefs]
    );

    useEffect(() => {
        setInnerBriefs(briefs);
    }, [briefs]);

    useEffect(() => {
        const filteredBriefs = filterBriefsBySearchInput(briefs?.briefs, searchInput);
        setInnerBriefs(prev => ({...prev, briefs: filteredBriefs}));
    }, [searchInput, briefs]);

    const onViewBriefClick = useCallback(brief => {
        setSelectedBriefAction({
            ...brief,
            date: moment(brief.dtstart),
            time: moment(brief.dtstart)
        });
        setSelectedAction(companySelectedActions.viewBrief);
    }, []);

    const onWatchBid = useCallback(
        async bid => {
            history.push({pathname: `/bids/admin/${bid.bidId}`, state: {bidId: bid.bidId}});
        },
        [history]
    );

    const onCancelBid = useCallback(
        async bid => {
            const res = await cancelBid(bid?.briefId, bid?.bidId);
            if (res?.error) {
                return;
            }
            fetchBriefs();
        },
        [fetchBriefs]
    );

    return (
        <>
            <BriefBidsReceivedModal
                showProviderPhone={true}
                visible={selectedActions === companySelectedActions.openOffers}
                onClose={closeActionModal}
                bids={selectedBriefAction?.bids}
                onBidClick={bid =>
                    history.push({
                        pathname: `/${ADMIN_PATH_NAME}/briefs/${bid.briefId}`,
                        state: {bidId: bid.bidId}
                    })
                }
                btnBitTex="לאישור"
                bidStatusToColorCompanyMap={BidStatusToColorAdmin}
                actions={[
                    {
                        key: "view-bid",
                        title: "לצפייה בהצעת מחיר",
                        onClick: onWatchBid
                    },
                    {
                        key: "edit-bid",
                        title: "עריכת הצעת מחיר",
                        disabled: bid => bid.status === BidStatus.cancelled || bid.status === BidStatus.accepted,
                        onClick: bid =>
                            history.push({
                                pathname: `/${ADMIN_PATH_NAME}/briefs/${bid.briefId}`,
                                state: {bidId: bid.bidId}
                            })
                    },
                    {
                        key: "cancel-bid",
                        title: "ביטול הצעת מחיר",
                        onClick: onCancelBid,
                        shouldFetch: true,
                        className: "provider-briefs-cancel-brief-btn",
                        disabled: bid => bid.status === BidStatus.cancelled || bid.status === BidStatus.accepted
                    }
                ]}
                brief={selectedBriefAction}
            />

            <BriefFormModal
                visible={selectedActions === companySelectedActions.viewBrief}
                initValues={selectedBriefAction}
                onClose={closeActionModal}
                onSave={onConfirmBrif}
                disabledFields={
                    selectedBriefAction?.status !== BriefStatus.pending || selectedBriefAction?.watchedByAdmin
                        ? [
                              "address",
                              "floor",
                              "participantsAmount",
                              "date",
                              "time",
                              "serviceId",
                              "maxPrice",
                              "poNumber",
                              "description"
                          ]
                        : []
                }
            />

            <BriefChangesHistoryModal
                visible={!!historyBrief}
                briefId={historyBrief?.briefId}
                onClose={() => setHistoryBrief(null)}
                isAdmin={true}
            />

            <DeclineOrderModal
                visible={selectedActions === companySelectedActions.canceledBrief}
                onSave={onDeclineBrief}
                onClose={closeActionModal}
                header={BRIEF_DECLINE_MODAL_HEADER}
                subTitle={BRIEF_DECLINE_MODAL_SUBTITLE}
                declineReasons={briefDeclineOptionsToText}
                showWhoToNotify={true}
                defaultWhoToSendUpdateEmail={[editOrderNotificationsReceivers.company]}
                declineReasonLabel="סיבת ביטול"
                declineCommentLabel={null}
            />

            <TableDateRangeTable
                title="ניהול בריפים"
                onInputSearch={setSearchInput}
                tableClassName="company-briefs"
                pagination={{
                    position: ["bottomRight"],
                    showSizeChanger: false,
                    current: currentPage,
                    defaultPageSize: briefPageSize,
                    total: innerBriefs?.totalCount,
                    showTotal: total => <span>סה״כ {total}</span>,
                    onChange: page => setCurrentPage(page)
                }}
                isLoadingOrderTable={isLoadingBriefs}
                shortcutSearchOptions={BriefsShortcuts}
                data={innerBriefs?.briefs ?? []}
                onShortcutSearchChange={setIsWaitingForAdminApproval}
                onChange={(_, filters) => {
                    if (filters?.systemId !== undefined) {
                        setBriefsSystemIdOrder(filters.systemId || []);
                    }
                }}
                columns={columns({
                    currentPage,
                    briefStatusToColorMapper: BriefStatusToColorAdmin,
                    allCSMs: systemUsers,
                    onChangeCSM: onChangeCSM,
                    loadingUpdateBrief: loadingUpdateBrief,
                    onSaveComment: onUpdateAdminComment,
                    setBriefsDtstartOrder,
                    briefsDtstartOrder,
                    setBriefsCreatedAtOrder,
                    briefsCreatedAtOrder,
                    setBriefsSystemIdOrder,
                    briefsSystemIdOrder,
                    manageBriefs: [
                        {
                            shouldShow: brief =>
                                (brief?.status === BriefStatus.pending && !brief?.watchedByAdmin) ||
                                (brief?.status !== BriefStatus.cancelled &&
                                    brief?.providerIds?.every(pid => brief?.removedProviderIds?.includes(pid))),
                            text: "אישור בריף לספקים",
                            onClick: brief => onViewBriefClick(brief)
                        },
                        {
                            shouldShow: brief =>
                                brief?.bids?.length &&
                                brief?.bids?.some(bid => !bid.watchedByAdmin) &&
                                brief?.status === BriefStatus.haveBids,
                            text: "שחרור הצעת מחיר ללקוח",
                            onClick: brief => {
                                setSelectedBriefAction(brief);
                                setSelectedAction(companySelectedActions.openOffers);
                            }
                        }
                    ]
                })}
                actions={[
                    {
                        title: "צפייה בבריף",
                        onClick: onViewBriefClick
                    },
                    {
                        title: "צפייה בסטטוס הצעות",
                        onClick: brief => {
                            setSelectedBriefAction(brief);
                            setSelectedAction(companySelectedActions.openOffers);
                        }
                    },
                    {
                        title: "ביטול בריף",
                        className: "company-briefs-cancel-brief-btn",
                        onClick: brief => onCancelBrief(brief),
                        disabled: brief =>
                            brief.status === BriefStatus.cancelled || brief.status === BriefStatus.accepted
                    },
                    {
                        title: "היסטוריה",
                        onClick: brief => setHistoryBrief(brief)
                    }
                ]}
                disableFutureDates={false}
            />
        </>
    );
};
